/**
 * Lace & Luna mega menu — desktop hover + mobile drawer accordion.
 */
class LaceMegaMenu {
  constructor() {
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
    this.menus = [...document.querySelectorAll('.lace-mega-menu .mega-menu')];
    this.header = document.querySelector('.header-wrapper');
    this.drawer = document.querySelector('.menu-drawer');
    this.closeTimer = null;
    this.activeDetails = null;
    this.backdrop = null;

    if (!this.menus.length) return;

    this.ensureBackdrop();
    this.bindSummaryLinks();
    this.setHeaderOffset();
    window.addEventListener('resize', () => this.setHeaderOffset());
    window.addEventListener(
      'scroll',
      () => {
        if (!this.mediaQuery.matches || !this.activeDetails) return;
        this.setHeaderOffset();
      },
      { passive: true }
    );
    this.mediaQuery.addEventListener('change', () => this.syncMode());
    this.syncMode();
  }

  isMobileDrawerMenu(details) {
    return !this.mediaQuery.matches && !!details.closest('.menu-drawer');
  }

  ensureBackdrop() {
    if (document.querySelector('.lace-mega-menu__backdrop')) return;
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'lace-mega-menu__backdrop';
    this.backdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.backdrop);
    this.backdrop.addEventListener('mouseenter', () => this.clearCloseTimer());
    this.backdrop.addEventListener('mouseleave', () => {
      if (this.activeDetails) this.scheduleClose(this.activeDetails);
    });
  }

  bindSummaryLinks() {
    this.menus.forEach((details) => {
      const summary = details.querySelector('summary');
      const link = summary?.querySelector('.mega-menu__summary-link');
      if (!summary || !link) return;

      let touchStartY = 0;
      let touchMoved = false;

      summary.addEventListener(
        'touchstart',
        (event) => {
          if (!this.isMobileDrawerMenu(details)) return;
          touchStartY = event.touches[0]?.clientY ?? 0;
          touchMoved = false;
        },
        { passive: true }
      );

      summary.addEventListener(
        'touchmove',
        (event) => {
          if (!this.isMobileDrawerMenu(details)) return;
          const y = event.touches[0]?.clientY ?? touchStartY;
          if (Math.abs(y - touchStartY) > 8) touchMoved = true;
        },
        { passive: true }
      );

      const handleMobileToggle = (event) => {
        if (!this.isMobileDrawerMenu(details)) return false;
        if (touchMoved) {
          event.preventDefault();
          touchMoved = false;
          return true;
        }
        event.preventDefault();
        event.stopPropagation();
        this.toggleMobileDrawerMenu(details);
        return true;
      };

      link.addEventListener('click', (event) => {
        if (handleMobileToggle(event)) return;
        event.stopPropagation();
      });

      summary.addEventListener('click', (event) => {
        if (handleMobileToggle(event)) return;

        if (!this.mediaQuery.matches) return;
        if (event.target.closest('.mega-menu__summary-link')) return;
        event.preventDefault();
      });
    });
  }

  resetMobileDrawerScroll(details) {
    if (this.mediaQuery.matches || !details?.closest('.menu-drawer')) return;

    const container = this.drawer;
    if (!container) return;

    const scrollToSectionTop = () => {
      const target =
        details.querySelector('.mega-menu__hero') ||
        details.querySelector('.mega-menu__content') ||
        details;
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      container.scrollTop += targetTop - containerTop;
    };

    scrollToSectionTop();
    requestAnimationFrame(scrollToSectionTop);
  }

  toggleMobileDrawerMenu(details) {
    const isOpen = details.hasAttribute('open');

    this.menus.forEach((menu) => {
      if (menu === details || !menu.closest('.menu-drawer')) return;
      menu.removeAttribute('open');
      menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    if (isOpen) {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      return;
    }

    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    this.resetMobileDrawerScroll(details);
  }

  setHeaderOffset() {
    if (!this.header) return;
    const bottom = Math.floor(this.header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${bottom}px`);
  }

  syncMode() {
    if (this.mediaQuery.matches) {
      this.enableHover();
      this.closeMobileDrawerMenus();
    } else {
      this.disableHover();
      this.setBodyOpen(false);
    }
  }

  closeMobileDrawerMenus() {
    this.menus.forEach((menu) => {
      if (!menu.closest('.menu-drawer')) return;
      menu.removeAttribute('open');
      menu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  menuZones(details) {
    const host = details.closest('header-menu') || details.parentElement;
    const panel = details.querySelector('.mega-menu__content');
    return [host, panel].filter(Boolean);
  }

  enableHover() {
    this.menuBindings = this.menus.map((details) => {
      const zones = this.menuZones(details);
      const open = () => this.openMenu(details);
      const scheduleClose = () => this.scheduleClose(details);

      zones.forEach((zone) => {
        zone.addEventListener('mouseenter', open);
        zone.addEventListener('mouseleave', scheduleClose);
      });

      return { details, zones, open, scheduleClose };
    });
  }

  disableHover() {
    if (!this.menuBindings) return;

    this.menuBindings.forEach(({ zones, open, scheduleClose }) => {
      zones.forEach((zone) => {
        zone.removeEventListener('mouseenter', open);
        zone.removeEventListener('mouseleave', scheduleClose);
      });
    });

    this.menuBindings = null;
    this.clearCloseTimer();
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  setBodyOpen(isOpen) {
    document.body.classList.toggle('lace-mega-menu-open', isOpen);
  }

  openMenu(details) {
    this.clearCloseTimer();
    this.activeDetails = details;

    this.menus.forEach((menu) => {
      if (menu !== details) {
        this.closeMenu(menu);
      }
    });

    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    this.setBodyOpen(true);
    this.setHeaderOffset();
  }

  closeMenu(details) {
    details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');

    if (this.activeDetails === details) {
      this.activeDetails = null;
      this.setBodyOpen(false);
    }
  }

  scheduleClose(details) {
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => {
      this.closeMenu(details);
    }, 180);
  }
}

if (!customElements.get('lace-mega-menu-init')) {
  document.addEventListener('DOMContentLoaded', () => new LaceMegaMenu());
}

/* Mobile drawer sub-accordions — row tap expands parents; leaf links navigate */
(function () {
  const mq = window.matchMedia('(min-width: 990px)');
  let groups = [];

  const caretIcon =
    '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

  function findPrimaryLink(parent) {
    return parent.querySelector(
      ':scope > .mega-menu__card-link, :scope > .mega-menu__link--level-2, :scope > .mega-menu__link--level-3, :scope > a.link'
    );
  }

  function teardown() {
    groups.forEach(({ parent, row, toggle, onToggle, onParentLinkClick, primaryLink, panel }) => {
      toggle.removeEventListener('click', onToggle);
      primaryLink.removeEventListener('click', onParentLinkClick);
      toggle.remove();
      if (row && primaryLink && panel) {
        parent.insertBefore(primaryLink, panel);
        row.remove();
      }
      parent.classList.remove('mega-menu__mobile-accordion', 'is-collapsed', 'is-expanded');
      delete parent.dataset.mobileAccordionInit;
    });
    groups = [];
  }

  function setup() {
    const drawer = document.querySelector('.menu-drawer');
    if (mq.matches || !drawer || groups.length) return;

    const registerAccordion = (parent, panel) => {
      if (!parent || !panel || parent.dataset.mobileAccordionInit === 'true') return;

      const primaryLink = findPrimaryLink(parent);
      if (!primaryLink) return;

      parent.dataset.mobileAccordionInit = 'true';
      parent.classList.add('mega-menu__mobile-accordion', 'is-collapsed');
      primaryLink.setAttribute('aria-expanded', 'false');

      const row = document.createElement('div');
      row.className = 'mega-menu__mobile-accordion__row';

      parent.insertBefore(row, primaryLink);
      row.appendChild(primaryLink);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'mega-menu__mobile-accordion__toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Show subcategories');
      toggle.innerHTML = caretIcon;

      const onToggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const expanded = parent.classList.toggle('is-expanded');
        parent.classList.toggle('is-collapsed', !expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        primaryLink.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute('aria-label', expanded ? 'Hide subcategories' : 'Show subcategories');
      };

      const onParentLinkClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle(event);
      };

      primaryLink.addEventListener('click', onParentLinkClick);
      toggle.addEventListener('click', onToggle);
      row.appendChild(toggle);

      groups.push({ parent, row, toggle, onToggle, onParentLinkClick, primaryLink, panel });
    };

    drawer.querySelectorAll('.lace-mega-menu .mega-menu__card-inner--has-subs').forEach((parent) => {
      registerAccordion(parent, parent.querySelector(':scope > .mega-menu__card-sublist'));
    });

    drawer.querySelectorAll('.lace-mega-menu .mega-menu__link-group--has-nested').forEach((parent) => {
      registerAccordion(parent, parent.querySelector(':scope > .mega-menu__nested-flyout'));
    });

    drawer.querySelectorAll('.lace-mega-menu .mega-menu__link-group').forEach((parent) => {
      if (parent.classList.contains('mega-menu__link-group--has-nested')) return;
      const panel = parent.querySelector(':scope > .mega-menu__sublist');
      if (panel) registerAccordion(parent, panel);
    });

    drawer.querySelectorAll('.lace-mega-menu .mega-menu__column').forEach((parent) => {
      const panel = parent.querySelector(':scope > .mega-menu__column-links');
      if (panel) registerAccordion(parent, panel);
    });
  }

  function sync() {
    if (mq.matches) teardown();
    else setup();
  }

  document.addEventListener('DOMContentLoaded', sync);
  mq.addEventListener('change', sync);
})();
